import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    ICreateUser,
    IResponsible,
    IResponsibleUser,
    IUpdateResponsible,
    IUser,
    ResponsibleType
} from '@shared/interfaces';
import { SortAlphabeticallyPipe } from '@shared/pipes';
import { UserRole } from '../../../shared/enums/UserRole';
import { ResponsibleService, SnackService } from '@shared/services';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';

@Component({
    selector: 'app-org-modify',
    templateUrl: './org-modify.component.html',
    styleUrls: ['./org-modify.component.scss'],
    providers: [SortAlphabeticallyPipe],
    standalone: false
})
export class OrgModifyComponent implements OnInit {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    formGroup: UntypedFormGroup;
    ResponsibleType = ResponsibleType;

    constructor(
        private fb: UntypedFormBuilder,
        private createDialogRef: MatDialogRef<OrgModifyComponent>,
        private aSortPipe: SortAlphabeticallyPipe,
        private snackBar: SnackService,
        private responsibleService: ResponsibleService,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            parentItem: IResponsible;
            selectedItem?: IResponsible;
        }
    ) {}

    get item(): IUpdateResponsible {
        return this.formGroup.getRawValue() as IUpdateResponsible;
    }

    get isSag(): boolean {
        return this.item ? this.item.responsibleType === ResponsibleType.sag : false;
    }

    get selectedUsers(): IResponsibleUser[] {
        return [
            ...(this.item.mainResponsible || []),
            ...(this.item.mainSpecialists || []),
            ...(this.item.otherResponsible || []),
            ...(this.item.otherSpecialists || [])
        ];
    }

    get selectedItem(): IResponsible {
        return this.data?.selectedItem;
    }

    set selectedItem(val: IResponsible) {
        this.data.selectedItem = val;
    }

    get isCreate(): boolean {
        return !this.selectedItem;
    }

    changeAssignedCountries(event: string[]) {
        this.aSortPipe.transform(event);
    }

    get parentItem(): IResponsible {
        return this.data?.parentItem;
    }

    ngOnInit() {
        this.buildForm();
    }

    get mainResponsible(): IResponsibleUser[] {
        return this.selectedItem?.responsible?.filter(e => e.fullAccess) ?? null;
    }

    get otherResponsible(): IResponsibleUser[] {
        return this.selectedItem?.responsible?.filter(e => !e.fullAccess) ?? null;
    }

    get mainSpecialists(): IResponsibleUser[] {
        return this.selectedItem?.specialists?.filter(e => e.fullAccess) ?? null;
    }

    get otherSpecialists(): IResponsibleUser[] {
        return this.selectedItem?.specialists?.filter(e => !e.fullAccess) ?? null;
    }

    onSubmit() {
        if (this.formGroup.valid) {
            const newUsers = (this.selectedUsers as IUser[])
                .filter(e => e.isExternal)
                .map(e => ({ userRole: UserRole.standard, userData: e.userData }) as ICreateUser);
            const matchSamePerson = this.item.mainResponsible.some(item =>
                this.item.mainSpecialists.find(x => x.id === item.id)
            );
            if (matchSamePerson) {
                this.snackBar.openSnackBarError('organisation.modify.differentPerson.snack');
            } else {
                this.createDialogRef.close({ ...this.item, newUsers });
            }
        }
    }

    private buildForm() {
        this.formGroup = this.fb.group({
            id: this.selectedItem?.id,
            orgDescription: [this.selectedItem?.orgDescription ?? null, Validators.required],
            orgCode: [{ value: this.selectedItem?.orgCode ?? null, disabled: !this.isCreate }, Validators.required],
            mainResponsible: [this.mainResponsible ?? [], Validators.required],
            mainSpecialists: [this.mainSpecialists ?? [], Validators.required],
            otherResponsible: [this.otherResponsible ?? []],
            otherSpecialists: [this.otherSpecialists ?? []],
            responsibleType: [
                this.selectedItem?.responsibleType ?? this.parentItem?.responsibleType ?? ResponsibleType.sag,
                Validators.required
            ],
            assignedCountries: [this.selectedItem?.assignedCountries || []],
            orgPath: [this.selectedItem?.orgPath ?? this.parentItem?.orgPath ?? []],
            parentOrgCode:
                this.selectedItem?.parentOrgCode ??
                this.parentItem?.orgCode ??
                (this.parentItem?.responsibleType ? `responsibility-${this.parentItem?.responsibleType}` : null),
            parentId:
                this.selectedItem?.parentId ||
                this.parentItem?.id ||
                (this.parentItem?.responsibleType ? `responsibility-${this.parentItem?.responsibleType}` : null),
            areCode: this.selectedItem?.areCode ?? null,
            orgLevel: this.selectedItem?.orgLevel ?? (this.parentItem?.orgLevel ?? 0) + 1
        });
    }
}
